<template>
  <div class="tasks-container">
    <div class="page-header">
      <h2>有偿任务管理</h2>
      <p class="subtitle">管理用户发布的有偿任务</p>
    </div>

    <!-- 统计卡片 -->
    <el-row :gutter="20" class="stat-cards">
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-icon total"><el-icon><List /></el-icon></div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.total }}</div>
            <div class="stat-label">总任务</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-icon pending"><el-icon><Clock /></el-icon></div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.pending }}</div>
            <div class="stat-label">待审核</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-icon active"><el-icon><Loading /></el-icon></div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.active }}</div>
            <div class="stat-label">进行中</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-icon completed"><el-icon><CircleCheck /></el-icon></div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.completed }}</div>
            <div class="stat-label">已完成</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 搜索筛选 -->
    <el-card shadow="never" class="filter-card">
      <el-form :inline="true" :model="queryParams">
        <el-form-item label="状态">
          <el-select v-model="queryParams.status" placeholder="全部" clearable style="width: 120px">
            <el-option label="待审核" value="pending" />
            <el-option label="进行中" value="active" />
            <el-option label="已完成" value="completed" />
            <el-option label="已取消" value="cancelled" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="loadList">搜索</el-button>
          <el-button @click="resetQuery">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 数据表格 -->
    <el-card shadow="never" class="table-card">
      <el-table :data="tableData" v-loading="loading" stripe>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="title" label="任务标题" min-width="150" show-overflow-tooltip />
        <el-table-column label="发布者" width="120">
          <template #default="{ row }">{{ row.user?.realName || row.user?.mobile || '-' }}</template>
        </el-table-column>
        <el-table-column prop="reward" label="奖励金额" width="120">
          <template #default="{ row }">¥{{ row.reward }}</template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="statusMap[row.status]?.type">{{ statusMap[row.status]?.label }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="170">
          <template #default="{ row }">{{ formatDate(row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" link @click="showDetail(row)">详情</el-button>
            <el-dropdown v-if="row.status === 'pending'" trigger="click" @command="(cmd) => handleStatus(row, cmd)">
              <el-button type="success" size="small">审核</el-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="active">批准发布</el-dropdown-item>
                  <el-dropdown-item command="cancelled">拒绝</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrapper">
        <el-pagination v-model:current-page="queryParams.page" v-model:page-size="queryParams.pageSize" :total="total" :page-sizes="[10, 20, 50]" layout="total, sizes, prev, pager, next" @change="loadList" />
      </div>
    </el-card>

    <!-- 详情弹窗 -->
    <el-dialog v-model="detailVisible" title="任务详情" width="600px">
      <el-descriptions :column="2" border v-if="currentItem">
        <el-descriptions-item label="ID">{{ currentItem.id }}</el-descriptions-item>
        <el-descriptions-item label="状态"><el-tag :type="statusMap[currentItem.status]?.type">{{ statusMap[currentItem.status]?.label }}</el-tag></el-descriptions-item>
        <el-descriptions-item label="标题" :span="2">{{ currentItem.title }}</el-descriptions-item>
        <el-descriptions-item label="发布者">{{ currentItem.user?.realName || currentItem.user?.mobile }}</el-descriptions-item>
        <el-descriptions-item label="奖励金额">¥{{ currentItem.reward }}</el-descriptions-item>
        <el-descriptions-item label="描述" :span="2">{{ currentItem.description || '-' }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ formatDate(currentItem.createdAt) }}</el-descriptions-item>
        <el-descriptions-item label="完成时间">{{ currentItem.completedAt ? formatDate(currentItem.completedAt) : '-' }}</el-descriptions-item>
      </el-descriptions>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { List, Clock, Loading, CircleCheck } from '@element-plus/icons-vue'
import { paidTasksApi } from '@/api/modules/paid-tasks'

const loading = ref(false)
const tableData = ref([])
const total = ref(0)
const stats = ref({ total: 0, pending: 0, active: 0, completed: 0 })
const queryParams = ref({ page: 1, pageSize: 10, status: '' })
const detailVisible = ref(false)
const currentItem = ref(null)

const statusMap = {
  pending: { label: '待审核', type: 'warning' },
  active: { label: '进行中', type: 'primary' },
  completed: { label: '已完成', type: 'success' },
  cancelled: { label: '已取消', type: 'info' }
}

const formatDate = (date) => date ? new Date(date).toLocaleString('zh-CN') : '-'

const loadList = async () => {
  loading.value = true
  try {
    const res = await paidTasksApi.getList(queryParams.value)
    if (res.data?.code === 200) {
      tableData.value = res.data.data.items || []
      total.value = res.data.data.total || 0
    }
  } finally {
    loading.value = false
  }
}

const loadStats = async () => {
  const res = await paidTasksApi.getStatistics()
  if (res.data?.code === 200) {
    stats.value = res.data.data
  }
}

const resetQuery = () => {
  queryParams.value = { page: 1, pageSize: 10, status: '' }
  loadList()
}

const showDetail = async (row) => {
  const res = await paidTasksApi.getDetail(row.id)
  if (res.data?.code === 200) {
    currentItem.value = res.data.data
    detailVisible.value = true
  }
}

const handleStatus = async (row, status) => {
  const res = await paidTasksApi.updateStatus(row.id, status)
  if (res.data?.code === 200) {
    ElMessage.success(status === 'active' ? '已批准发布' : '已拒绝')
    loadList()
    loadStats()
  }
}

onMounted(() => {
  loadList()
  loadStats()
})
</script>

<style scoped>
.tasks-container { padding: 20px; }
.page-header { margin-bottom: 20px; }
.page-header h2 { margin: 0; font-size: 24px; font-weight: 600; color: #1f2937; }
.subtitle { margin: 8px 0 0; color: #6b7280; font-size: 14px; }
.stat-cards { margin-bottom: 20px; }
.stat-card { display: flex; align-items: center; }
.stat-card :deep(.el-card__body) { display: flex; align-items: center; width: 100%; padding: 0; }
.stat-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 24px; margin-right: 16px; }
.stat-icon.total { background: linear-gradient(135deg, #f3f4f6, #e5e7eb); color: #4b5563; }
.stat-icon.pending { background: linear-gradient(135deg, #fef3c7, #fde68a); color: #d97706; }
.stat-icon.active { background: linear-gradient(135deg, #dbeafe, #bfdbfe); color: #2563eb; }
.stat-icon.completed { background: linear-gradient(135deg, #d1fae5, #a7f3d0); color: #059669; }
.stat-content { flex: 1; }
.stat-value { font-size: 24px; font-weight: 700; color: #1f2937; }
.stat-label { font-size: 14px; color: #6b7280; margin-top: 4px; }
.filter-card { margin-bottom: 20px; border-radius: 12px; }
.table-card { border-radius: 12px; }
.pagination-wrapper { display: flex; justify-content: flex-end; margin-top: 20px; }
</style>
